import { useState } from "react";
import { IconButton, Tooltip } from "@mui/material";
import Iconify from "../../../minimals/components/iconify";
import CreateRemovalRequestModal from "../RemovalRequest/CreateRemovalRequestModal";
import { BlacklistEntityType, RemovalRequest, RemovalRequestStatus } from "../types";
import RemovalRequestPendingIconButton from "../RemovalRequest/RemovalRequestPendingIconButton";
import RemovalRequestApprovedIconButton from "../RemovalRequest/RemovalRequestApprovedIconButton";
import { StringParam, useQueryParam } from "use-query-params";

interface RemovalRequestCellProps {
  entityId: number;
  entityType: BlacklistEntityType;
  removalRequest: RemovalRequest | null;
  refreshData: () => Promise<void>;
}

export const RemovalRequestCell = ({ entityId, entityType, removalRequest, refreshData }: RemovalRequestCellProps) => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedEntityId, setSelectedEntityId] = useQueryParam("entityId", StringParam);

  const handleOpenRequest = () => {
    setSelectedEntityId(String(entityId));
  };

  if (removalRequest) {
    return removalRequest.status === RemovalRequestStatus.Pending ? (
      <Tooltip title="Removal request pending manager approval">
        <span>
          <RemovalRequestPendingIconButton onClick={handleOpenRequest} />
        </span>
      </Tooltip>
    ) : (
      <Tooltip title="Removed from blacklist. Click to see details.">
        <span>
          <RemovalRequestApprovedIconButton onClick={handleOpenRequest} />
        </span>
      </Tooltip>
    );
  }

  return (
    <>
      <Tooltip title="Request Removal of Blacklist">
        <IconButton color="error" onClick={() => setIsCreateModalOpen(true)}>
          <Iconify icon="eva:trash-2-outline" width={24} />
        </IconButton>
      </Tooltip>
      {isCreateModalOpen && (
        <CreateRemovalRequestModal
          open={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          entityId={entityId}
          entityType={entityType}
          onActionComplete={refreshData}
        />
      )}
    </>
  );
};

export const createRemovalRequestColumn = <T extends { id: number; removalRequest: RemovalRequest | null }>(
  refreshData: () => Promise<void>,
  entityType: BlacklistEntityType
) => ({
  accessorKey: "removalRequest",
  header: "Removal Request",
  Cell: ({ row }: { row: { original: T } }) => {
    const removalRequest = row.original.removalRequest;
    const entityId = row.original.id;
    return (
      <RemovalRequestCell
        entityId={entityId}
        entityType={entityType}
        removalRequest={removalRequest}
        refreshData={refreshData}
      />
    );
  },
  size: 50,
  muiTableBodyCellProps: { sx: { verticalAlign: "top" } }
});
